// Admin topbar — sticky header with shield branding and mobile menu toggle.
// Visual port of frontend/src/app/admin/_components/admin-header.tsx.
// Metric cells poll /api/v2/admin/dashboard via useAdminDashboardSnapshot
// (10s foreground interval) — they were hardcoded to "0" before, which is why
// Online/Trades hoje never moved (operator bug report 2026-06-11).
import { Menu, Shield } from "lucide-react";
import { MatrixRateLimitBar } from "../matrix-stats/components/MatrixRateLimitBar";
import { useAdminDashboardSnapshot } from "../dashboard/hooks";
import { sumTodayTrades } from "../dashboard/lib/dashboardTotals";
import { formatNumber } from "@/lib/format/currency";

interface AdminTopbarProps {
  onMenuClick: () => void;
}

function metricCellValue(value: number | null | undefined): string {
  return value == null ? "—" : formatNumber(value);
}

export function AdminTopbar({
  onMenuClick,
}: AdminTopbarProps): React.JSX.Element {
  const { data: snapshot } = useAdminDashboardSnapshot();
  return (
    <header className="sticky top-0 z-40 bg-background/80 border-b border-border backdrop-blur">
      <TopbarContent onMenuClick={onMenuClick} snapshot={snapshot} />
      <MatrixRateLimitBar />
    </header>
  );
}

interface TopbarContentProps {
  readonly onMenuClick: () => void;
  readonly snapshot: ReturnType<typeof useAdminDashboardSnapshot>["data"];
}

function TopbarContent(props: TopbarContentProps): React.JSX.Element {
  return (
    <div className="relative flex items-center justify-between px-4 h-12">
      <MobileMenuButton onClick={props.onMenuClick} />
      <DesktopBrand />
      <MobileBrand />
      <TopbarMetrics snapshot={props.snapshot} />
    </div>
  );
}

function MobileMenuButton(props: {
  readonly onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      onClick={props.onClick}
      data-testid="admin-menu-button"
      className="admin-mobile-menu-trigger flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface transition-all hover:bg-surface md:hidden"
      aria-label="Abrir menu"
    >
      <Menu className="w-4 h-4 text-foreground/40" />
    </button>
  );
}

function DesktopBrand(): React.JSX.Element {
  return (
    <div className="hidden lg:!flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-surface border border-border flex items-center justify-center">
        <Shield className="w-4 h-4 text-brand-cyan" />
      </div>
      <div>
        <div className="text-sm font-semibold text-foreground">
          Painel Admin
        </div>
        <div className="text-[10px] text-foreground/40">
          Monitoramento e auditoria
        </div>
      </div>
    </div>
  );
}

function MobileBrand(): React.JSX.Element {
  return (
    <div className="lg:hidden absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 pointer-events-none">
      <Shield className="w-4 h-4 text-brand-cyan" />
      <span className="text-xs font-bold text-brand-cyan">Admin</span>
    </div>
  );
}

function TopbarMetrics(
  props: Pick<TopbarContentProps, "snapshot">,
): React.JSX.Element {
  return (
    <div className="hidden items-center gap-2 lg:!flex">
      <TopbarMetricCell
        testId="admin-topbar-online-users"
        label="Online"
        value={metricCellValue(props.snapshot?.onlineUsers)}
      />
      <TopbarMetricCell
        testId="admin-topbar-today-trades"
        label="Trades hoje"
        value={metricCellValue(sumTodayTrades(props.snapshot))}
      />
    </div>
  );
}

interface TopbarMetricCellProps {
  readonly testId: string;
  readonly label: string;
  readonly value: string;
}

function TopbarMetricCell(props: TopbarMetricCellProps): React.JSX.Element {
  return (
    <div
      data-testid={props.testId}
      className="rounded-md border border-border bg-surface px-2.5 py-1 text-right"
    >
      <div className="text-[9px] font-semibold uppercase tracking-wide text-foreground/30">
        {props.label}
      </div>
      <div className="text-xs font-semibold tabular-nums text-foreground/70">
        {props.value}
      </div>
    </div>
  );
}
